import { type Message, type ProfileSettings } from "@herskin/contracts";
import { MarkdownMessage } from "@herskin/markdown";
import { memo } from "react";
import { MessageAttachments } from "./MessageAttachments";
import { formatBeijingTime, messageTimeValue } from "./presentation";

interface MessageItemProps {
  message: Message;
  timeLabel: string | null;
  durationLabel: string | null;
  profile: ProfileSettings | null;
  agentName: string;
}

const TypingIndicator = memo(function TypingIndicator({ agentName }: { agentName: string }) {
  return (
    <div className="assistant-typing" role="status" aria-label={`${agentName}正在输入`}>
      <span aria-hidden="true" />
      <span aria-hidden="true" />
      <span aria-hidden="true" />
      <small>正在输入</small>
    </div>
  );
});

function MessageAvatar({
  profile,
  role,
  agentName,
}: {
  profile: ProfileSettings | null;
  role: Message["role"];
  agentName?: string;
}) {
  if (role !== "user" && role !== "assistant") return null;
  const identity = role === "user" ? profile?.owner : profile?.agent;
  const configuredAgentName = profile?.agent.displayName ?? "Hermes";
  const displayName =
    role === "assistant" ? (agentName ?? configuredAgentName) : identity?.displayName;
  // 头像设置目前属于 default Profile；其他 Profile 先用名称首字，避免错误冒用马思思头像。
  const avatarUrl =
    role === "assistant" && displayName !== configuredAgentName ? null : identity?.avatarUrl;
  const fallback = displayName?.trim().slice(0, 1) || (role === "user" ? "用" : "H");
  return (
    <span className={`message-avatar message-avatar--${role}`} aria-hidden="true">
      {avatarUrl ? <img src={avatarUrl} alt="" /> : <span>{fallback}</span>}
    </span>
  );
}

/** 单条消息的纯展示组件，与会话控制和网络状态解耦。 */
export const MessageItem = memo(function MessageItem({
  message,
  timeLabel,
  durationLabel,
  profile,
  agentName,
}: MessageItemProps) {
  const isEmptyStream =
    message.role === "assistant" && message.status === "streaming" && !message.content;
  const authorName =
    message.role === "user"
      ? (profile?.owner.displayName ?? "用户")
      : message.role === "assistant"
        ? agentName
        : "系统";
  return (
    <article className={`message message--${message.role}`}>
      <div className="message-author">
        <MessageAvatar profile={profile} role={message.role} agentName={agentName} />
        <span>{authorName}</span>
      </div>
      <div className="message-body">
        {isEmptyStream ? (
          <TypingIndicator agentName={agentName} />
        ) : message.role === "assistant" ? (
          <MarkdownMessage
            content={message.content}
            state={message.status === "streaming" ? "streaming" : "complete"}
          />
        ) : (
          <p>{message.content}</p>
        )}
        <MessageAttachments attachments={message.attachments} />
      </div>
      {timeLabel || durationLabel ? (
        <div className="message-meta">
          {timeLabel ? <time dateTime={messageTimeValue(message)}>{timeLabel}</time> : null}
          {timeLabel && durationLabel ? <span aria-hidden="true">·</span> : null}
          {durationLabel ? <span>{durationLabel}</span> : null}
        </div>
      ) : null}
    </article>
  );
});

/** Hermes 运行已开始但尚未形成消息正文时的稳定占位。 */
export function RunPlaceholder({
  startedAt,
  profile,
  agentName,
}: {
  startedAt: string | null | undefined;
  profile: ProfileSettings | null;
  agentName: string;
}) {
  return (
    <article className="message message--assistant message--placeholder">
      <div className="message-author">
        <MessageAvatar profile={profile} role="assistant" agentName={agentName} />
        <span>{agentName}</span>
      </div>
      <div className="message-body">
        <TypingIndicator agentName={agentName} />
      </div>
      <div className="message-meta">
        <span>{startedAt ? formatBeijingTime(startedAt) : "等待 Hermes 回传开始时间"}</span>
        <span aria-hidden="true">·</span>
        <span>生成中</span>
      </div>
    </article>
  );
}
